import React, {useEffect, useMemo, useRef, useState} from 'react';
import {message, Button} from 'antd';
import {useLocation, useNavigate} from 'react-router-dom';
import {ReportFileTypeEnum} from "../../../enums/ReportFileEnum";
import {ReportFileType} from "./graphql/queries/getReportFileListing";
import {useDeliveryReportFileQuery, ReportFileFiltersType} from "./hooks/useDeliveryReportFileQuery";
import {useReportOperators} from "./hooks/useReportOperators";
import WmsReportFileFilter from "../../wms/download/components/WmsReportFileFilter";
import WmsReportFileColumns from "../../wms/download/components/WmsReportFileColumns";
import { useTranslation } from 'react-i18next';
import { useLazyQuery } from '@apollo/client';
import {
    GET_BATCH_REPORT_DOWNLOAD_URLS,
    GetBatchReportDownloadUrlsResponse,
    GetBatchReportDownloadUrlsVariables
} from './graphql/queries/getBatchReportFileUrls';
import TablePaginationBar from '../../../components/TablePaginationBar';
import { getLastWeekDateRange } from '../../../utils/dateTimeUtils';
import {
    DELIVERY_DOWNLOAD_FROM_ASYNC_DASHBOARD_STATE_KEY,
    DELIVERY_DOWNLOAD_FROM_PARTNER_PENALTY_STATE_KEY,
} from '../../../constants/deliveryDownloadNavigation';

const DeliveryDownloadInitialValue: ReportFileFiltersType = {
    type: ReportFileTypeEnum.DELIVERY_TASK_TRACKING_NUMBER_EXPORT,
    name: undefined,
    reportDate: undefined,
    operatorId: undefined,
};

const DeliveryDownloadPage: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const initialTypeFromLocation = (): number => {
        const state = location.state as Record<string, unknown> | null;
        if (state?.[DELIVERY_DOWNLOAD_FROM_ASYNC_DASHBOARD_STATE_KEY] === true) return ReportFileTypeEnum.DASHBOARD_EXPORT;
        if (state?.[DELIVERY_DOWNLOAD_FROM_PARTNER_PENALTY_STATE_KEY] === true) return ReportFileTypeEnum.PARTNER_PENALTY_EXPORT;
        return ReportFileTypeEnum.DELIVERY_TASK_TRACKING_NUMBER_EXPORT;
    };

    const [tempFilters, setTempFilters] = useState<ReportFileFiltersType>(() => ({
        ...DeliveryDownloadInitialValue,
        type: initialTypeFromLocation(),
    }));
    const [filters, setFilters] = useState<ReportFileFiltersType>(() => ({
        ...DeliveryDownloadInitialValue,
        type: initialTypeFromLocation(),
    }));
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [selectedRows, setSelectedRows] = useState<ReportFileType[]>([]);
    const hasMergedDefaultFiltersRef = useRef(false);

    const {
        operatorOptions,
        loading: operatorsLoading,
        defaultFilters
    } = useReportOperators();

    const getEffectiveReportDate = (reportDate: [string | null, string | null] | null | undefined): [string | null, string | null] | null => {
        if (reportDate === null) return null;
        const isValid = reportDate &&
            Array.isArray(reportDate) &&
            reportDate.length === 2 &&
            reportDate[0] !== null &&
            reportDate[1] !== null;
        return isValid ? reportDate : getLastWeekDateRange() as [string | null, string | null];
    };

    const {
        reportFileData,
        loading: reportLoading,
        refetch,
        totalElements,
        buildQueryVariables
    } = useDeliveryReportFileQuery(
        {
            ...filters,
            reportDate: getEffectiveReportDate(filters.reportDate)
        },
        currentPage,
        pageSize,
        !defaultFilters
    );

    const handleSelectChange = (newSelectedRowKeys: React.Key[], newSelectedRows: ReportFileType[]) => {
        setSelectedRowKeys(newSelectedRowKeys);
        setSelectedRows(newSelectedRows);
    };

    useEffect(() => {
        if (!defaultFilters || hasMergedDefaultFiltersRef.current) {
            return;
        }
        const state = location.state as Record<string, unknown> | null;
        const fromAsyncModal = state?.[DELIVERY_DOWNLOAD_FROM_ASYNC_DASHBOARD_STATE_KEY] === true;
        const fromPartnerPenalty = state?.[DELIVERY_DOWNLOAD_FROM_PARTNER_PENALTY_STATE_KEY] === true;
        const resolvedType = fromAsyncModal
            ? ReportFileTypeEnum.DASHBOARD_EXPORT
            : fromPartnerPenalty
                ? ReportFileTypeEnum.PARTNER_PENALTY_EXPORT
                : ReportFileTypeEnum.DELIVERY_TASK_TRACKING_NUMBER_EXPORT;
        const filtersToSet = {
            ...defaultFilters,
            type: resolvedType,
            reportDate: undefined,
        };
        setTempFilters(filtersToSet);
        setFilters(filtersToSet);
        hasMergedDefaultFiltersRef.current = true;
        if (fromAsyncModal || fromPartnerPenalty) {
            navigate('.', { replace: true, state: {} });
        }
    }, [defaultFilters, location.state, navigate]);

    const handleResetFilters = () => {
        const resetFilters = {
            ...(defaultFilters || DeliveryDownloadInitialValue),
            type: ReportFileTypeEnum.DELIVERY_TASK_TRACKING_NUMBER_EXPORT,
        };
        setTempFilters(resetFilters);
        setFilters(resetFilters);
        setCurrentPage(1);
    };

    const handleFilter = () => {
        setFilters(tempFilters);
        setCurrentPage(1);
    };

    useEffect(() => {
        refetch(buildQueryVariables());
    }, [filters, currentPage, pageSize]);

    const handlePageChange = (page: number, pageSize: number) => {
        setCurrentPage(page);
        setPageSize(pageSize);
    };

    const { t } = useTranslation();

    const deliveryReportTypeOptions = useMemo(
        () => [
            { value: '', label: t('enum.reportTypeOptions.all') },
            { value: ReportFileTypeEnum.DASHBOARD_EXPORT.toString(), label: t('enum.reportTypeOptions.dashboardExport') },
            { value: ReportFileTypeEnum.DELIVERY_TASK_TRACKING_NUMBER_EXPORT.toString(), label: t('enum.reportTypeOptions.deliveryTaskTrackingNumberExport') },
            { value: ReportFileTypeEnum.ROUTE_ORDER_POOL_EXPORT.toString(), label: t('enum.reportTypeOptions.routeOrderPoolExport') },
            { value: ReportFileTypeEnum.PARTNER_PENALTY_EXPORT.toString(), label: t('enum.reportTypeOptions.partnerPenaltyExport') },
        ],
        [t]
    );

    const [downloadLoading, setDownloadLoading] = useState(false);
    const [getBatchDownloadUrls] = useLazyQuery<
        GetBatchReportDownloadUrlsResponse,
        GetBatchReportDownloadUrlsVariables
    >(GET_BATCH_REPORT_DOWNLOAD_URLS);

    const generateFilename = (index: number, reportName?: string) => {
        const timestamp = new Date().toISOString().split('T')[0];
        if (reportName) {
            const hasXlsx = /\.xlsx$/i.test(reportName);
            return hasXlsx ? reportName : `${reportName}.xlsx`;
        }
        return `Report_${index + 1}_${timestamp}.xlsx`;
    };

    const downloadFile = async (url: string, filename: string) => {
        const res = await fetch(url);
        const blob = await res.blob();
        const blobUrl = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(blobUrl);
    };

    const handleBatchDownload = async () => {
        if (selectedRows.length === 0) {
            message.warning(t('delivery.download.messages.selectReport'));
            return;
        }

        setDownloadLoading(true);
        try {
            const reportIds = selectedRows.map(report => parseInt(report.id.toString(), 10));

            const { data } = await getBatchDownloadUrls({
                variables: { reportIds },
                fetchPolicy: 'network-only'
            });

            if (!data?.getBatchReportDownloadUrls) {
                message.error(t('delivery.download.messages.fetchUrlFailed'));
                return;
            }

            const downloadUrls = data.getBatchReportDownloadUrls;

            if (downloadUrls.length === 0) {
                message.warning(t('delivery.download.messages.noAvailableUrl'));
                return;
            }

            if (downloadUrls.length > 1) {
                message.info(`${t('delivery.download.messages.batchDownloadStart')} ${downloadUrls.length}...`);
            }

            for (let i = 0; i < downloadUrls.length; i++) {
                const url = downloadUrls[i];
                const reportName = i < selectedRows.length ? selectedRows[i].name : undefined;
                const filename = generateFilename(i, reportName);

                try {
                    await downloadFile(url, filename);
                    await new Promise(res => setTimeout(res, 500));
                } catch (e) {
                    console.error(`Failed to download file ${i + 1}:`, e);
                    message.error(`${t('delivery.download.messages.downloadFileFailed')} ${i + 1}`);
                }
            }

            message.success(t('delivery.download.messages.batchDownloadSuccess'));

        } catch (error) {
            console.error('Batch download failed:', error);
            message.error(t('delivery.download.messages.batchDownloadFailed'));
        } finally {
            setDownloadLoading(false);
        }
    };

    return (
        <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
            <WmsReportFileFilter
                filters={tempFilters}
                setFilters={setTempFilters}
                handleFilter={handleFilter}
                handleResetFilters={handleResetFilters}
                selectedRows={selectedRows}
                operatorOptions={operatorOptions}
                reportTypeOptions={deliveryReportTypeOptions}
            />

            <div className="flex justify-between items-center mb-3 pl-0">
                <div className="flex items-center gap-2">
                </div>

                <div className="flex items-center gap-2">
                    <Button
                        type="primary"
                        onClick={handleBatchDownload}
                        loading={downloadLoading}
                        disabled={selectedRows.length === 0}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('components.button.download')} ({selectedRows.length})</span>
                    </Button>
                    <Button type="primary" onClick={handleFilter} className="w-auto min-w-24">
                        <span className="font-bold">{t('wms.download.filters.filter')}</span>
                    </Button>
                    <Button onClick={handleResetFilters} className="w-auto min-w-24">
                        <span className="font-bold">{t('wms.download.filters.reset')}</span>
                    </Button>
                </div>
            </div>

            <WmsReportFileColumns
                reportFileData={reportFileData}
                onSelectChange={handleSelectChange}
                selectedRowKeys={selectedRowKeys}
                loading={operatorsLoading || reportLoading}
            />

            <TablePaginationBar
                total={totalElements}
                current={currentPage}
                pageSize={pageSize}
                onChange={handlePageChange}
            />
        </div>
    );
};

export default DeliveryDownloadPage;
